import { supabase } from '@/integrations/supabase/client';
import { listS3Objects } from './s3Files';

// ============ CUSTOMER SYNC ============

interface CustomerForFolder {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

// Get all customers (users with customer role)
export async function getCustomersForFolders(): Promise<CustomerForFolder[]> {
  const { data, error } = await supabase
    .from('user_roles')
    .select(`
      user_id,
      users!inner (
        id,
        first_name,
        last_name,
        email
      )
    `)
    .eq('role', 'customer');

  if (error) {
    console.error('[rushesVirtualFolders] getCustomersForFolders error:', error);
    return [];
  }

  // Flatten the response
  return (data || []).map((row: any) => ({
    id: row.users.id,
    first_name: row.users.first_name,
    last_name: row.users.last_name,
    email: row.users.email,
  }));
}

// Build customer display name (trimmed to avoid trailing spaces)
function getCustomerDisplayName(customer: CustomerForFolder): string {
  const firstName = customer.first_name?.trim() || '';
  const lastName = customer.last_name?.trim() || '';
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) return firstName;
  if (lastName) return lastName;
  return customer.email.split('@')[0];
}

// ============ ID-EMBEDDED FOLDER NAMING ============

/**
 * Build a customer folder name with embedded client ID.
 * Format: "Prenom_Nom_shortId" (e.g. "Jean_Dupont_a1b2c3d4")
 */
export function buildCustomerFolderNameWithId(
  displayName: string,
  clientId: string
): string {
  const shortId = clientId.replaceAll('-', '').slice(0, 8);
  const sanitized = displayName.replaceAll(/\s+/g, '_');
  return `${sanitized}_${shortId}`;
}

/**
 * Build a topic folder name with embedded video content ID.
 * Format: "Titre-Sujet_shortId" (e.g. "Mon-Sujet_e5f6g7h8")
 */
export function buildTopicFolderNameWithId(
  topicTitle: string,
  videoContentId: string
): string {
  const shortId = videoContentId.replaceAll('-', '').slice(0, 8);
  const sanitized = sanitizeFolderName(topicTitle);
  return `${sanitized}_${shortId}`;
}

/**
 * Extract an embedded short ID from a folder name (last segment after '_').
 * Returns null if no valid ID found.
 */
export function extractIdFromFolderName(folderName: string): string | null {
  const regex = /_([a-f0-9]{8})$/i;
  const match = regex.exec(folderName);
  return match ? match[1] : null;
}

/**
 * Check if a full UUID starts with a given short ID (first 8 hex chars without dashes).
 */
export function matchesShortId(fullId: string, shortId: string): boolean {
  const normalizedFull = fullId.replaceAll('-', '').slice(0, 8).toLowerCase();
  return normalizedFull === shortId.toLowerCase();
}

// Sync customer folders - creates a root folder for each customer if not exists
export async function syncCustomerFolders(): Promise<{
  success: boolean;
  created: number;
  existing: number;
  error?: string;
}> {
  try {
    const customers = await getCustomersForFolders();
    let created = 0;
    let existing = 0;

    // Get all existing root folders
    const existingFolders = await getVirtualFolders(null);
    
    // Build a set of existing folder short IDs and names for matching
    const existingFolderShortIds = new Set<string>();
    const existingFolderNames = new Set<string>();
    for (const f of existingFolders) {
      existingFolderNames.add(f.name.toLowerCase());
      const shortId = extractIdFromFolderName(f.name);
      if (shortId) existingFolderShortIds.add(shortId.toLowerCase());
    }

    for (const customer of customers) {
      const shortId = customer.id.replaceAll('-', '').slice(0, 8).toLowerCase();
      
      // Check if a folder with this client ID already exists (new format)
      if (existingFolderShortIds.has(shortId)) {
        existing++;
        continue;
      }
      
      // Check legacy format (name without ID)
      const legacyName = getCustomerDisplayName(customer);
      if (existingFolderNames.has(legacyName.toLowerCase())) {
        existing++;
        continue;
      }

      // Create folder with embedded ID
      const folderName = buildCustomerFolderNameWithId(legacyName, customer.id);
      const result = await createVirtualFolder(folderName, null);
      if (result.success) {
        created++;
        existingFolderShortIds.add(shortId);
        existingFolderNames.add(folderName.toLowerCase());
      } else {
        console.warn(`[syncCustomerFolders] Failed to create folder for ${customer.email}:`, result.error);
      }
    }

    return { success: true, created, existing };
  } catch (error) {
    console.error('[syncCustomerFolders] Error:', error);
    return {
      success: false,
      created: 0,
      existing: 0,
      error: error instanceof Error ? error.message : 'Erreur inconnue',
    };
  }
}

// Types for virtual folders and files
export interface VirtualFolder {
  id: string;
  name: string;
  parent_folder_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface VirtualFile {
  id: string;
  s3_key: string;
  display_name: string;
  folder_id: string | null;
  file_size: number | null;
  last_modified: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Combined type for display (like S3Object but virtual)
export interface VirtualItem {
  id: string;
  name: string;
  isFolder: boolean;
  size: number;
  lastModified: string;
  // For files: the actual S3 key
  s3Key?: string;
  // For folders: the folder ID
  folderId?: string;
  parentFolderId?: string | null;
  // For folders: who created it (used for client-assignment filtering)
  createdBy?: string | null;
}

// ============ FOLDER OPERATIONS ============

export async function getVirtualFolders(parentFolderId: string | null = null): Promise<VirtualFolder[]> {
  const query = supabase
    .from('rushes_virtual_folders')
    .select('*')
    .order('name', { ascending: true });
  
  if (parentFolderId === null) {
    query.is('parent_folder_id', null);
  } else {
    query.eq('parent_folder_id', parentFolderId);
  }
  
  const { data, error } = await query;
  
  if (error) {
    console.error('[rushesVirtualFolders] getVirtualFolders error:', error);
    return [];
  }
  
  return data || [];
}

export async function getAllVirtualFolders(): Promise<VirtualFolder[]> {
  const { data, error } = await supabase
    .from('rushes_virtual_folders')
    .select('*')
    .order('name', { ascending: true });
  
  if (error) {
    console.error('[rushesVirtualFolders] getAllVirtualFolders error:', error);
    return [];
  }
  
  return data || [];
}

// Get only root folders (parent_folder_id is null) - lightweight query for client selector
export async function getRootVirtualFolders(): Promise<VirtualFolder[]> {
  const { data, error } = await supabase
    .from('rushes_virtual_folders')
    .select('id, name, parent_folder_id, created_at, updated_at, created_by')
    .is('parent_folder_id', null)
    .order('name', { ascending: true });
  
  if (error) {
    console.error('[rushesVirtualFolders] getRootVirtualFolders error:', error);
    return [];
  }
  
  return data || [];
}

/**
 * Get the IDs of clients assigned to a staff member.
 * Checks: pbm (via team_personal_brand_managers), ops_id, video_editor_id, monteur_id
 */
export async function getAssignedClientIds(staffUserId: string): Promise<string[]> {
  try {
    // Check PBM assignment (via team_personal_brand_managers join)
    const { data: pbmRecord } = await supabase
      .from('team_personal_brand_managers')
      .select('id')
      .eq('user_id', staffUserId)
      .limit(1)
      .maybeSingle();

    const queries: Promise<string[]>[] = [];

    const fetchByColumn = async (column: string, value: string): Promise<string[]> => {
      const { data } = await (supabase
        .from('users') as any)
        .select('id')
        .eq(column, value);
      return (data || []).map((u: any) => u.id);
    };

    // PBM clients
    if (pbmRecord) {
      queries.push(fetchByColumn('personal_brand_manager_id', pbmRecord.id));
    }

    // OPS, Video editor, Monteur clients
    queries.push(
      fetchByColumn('ops_id', staffUserId),
      fetchByColumn('video_editor_id', staffUserId),
      fetchByColumn('monteur_id', staffUserId)
    );

    const results = await Promise.all(queries);
    const allIds = results.flat();
    return [...new Set(allIds)];
  } catch (error) {
    console.error('[rushesVirtualFolders] getAssignedClientIds error:', error);
    return [];
  }
}

/**
 * Get assigned client IDs AND their full names for folder name matching.
 * Returns both IDs and normalized names (uppercase) for flexible matching.
 */
export async function getAssignedClientInfo(staffUserId: string): Promise<{ ids: string[]; names: string[] }> {
  try {
    const { data: pbmRecord } = await supabase
      .from('team_personal_brand_managers')
      .select('id')
      .eq('user_id', staffUserId)
      .limit(1)
      .maybeSingle();

    const queries: Promise<{ id: string; first_name: string | null; last_name: string | null }[]>[] = [];

    const fetchByColumn = async (column: string, value: string) => {
      const { data } = await (supabase
        .from('users') as any)
        .select('id, first_name, last_name')
        .eq(column, value);
      return data || [];
    };

    if (pbmRecord) {
      queries.push(fetchByColumn('personal_brand_manager_id', pbmRecord.id));
    }
    queries.push(
      fetchByColumn('ops_id', staffUserId),
      fetchByColumn('video_editor_id', staffUserId),
      fetchByColumn('monteur_id', staffUserId)
    );

    const results = await Promise.all(queries);
    const allClients = results.flat();

    const uniqueMap = new Map<string, { first_name: string | null; last_name: string | null }>();
    for (const c of allClients) {
      if (!uniqueMap.has(c.id)) uniqueMap.set(c.id, c);
    }

    const ids = [...uniqueMap.keys()];
    const names: string[] = [];
    for (const [, c] of uniqueMap) {
      const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim().toUpperCase();
      if (fullName) names.push(fullName);
      // Also add reversed format "LAST FIRST"
      const reversed = `${c.last_name || ''} ${c.first_name || ''}`.trim().toUpperCase();
      if (reversed && reversed !== fullName) names.push(reversed);
    }

    return { ids, names };
  } catch (error) {
    console.error('[rushesVirtualFolders] getAssignedClientInfo error:', error);
    return { ids: [], names: [] };
  }
}

export async function getVirtualFolderById(folderId: string): Promise<VirtualFolder | null> {
  const { data, error } = await supabase
    .from('rushes_virtual_folders')
    .select('*')
    .eq('id', folderId)
    .maybeSingle();
  
  if (error) {
    console.error('[rushesVirtualFolders] getVirtualFolderById error:', error);
    return null;
  }
  
  return data;
}

export async function createVirtualFolder(
  name: string,
  parentFolderId: string | null = null
): Promise<{ success: boolean; folder?: VirtualFolder; error?: string }> {
  const { data: userData } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('rushes_virtual_folders')
    .insert({
      name,
      parent_folder_id: parentFolderId,
      created_by: userData?.user?.id || null,
    })
    .select()
    .single();
  
  if (error) {
    console.error('[rushesVirtualFolders] createVirtualFolder error:', error);
    // Handle unique constraint violation
    if (error.code === '23505') {
      return { success: false, error: 'Un dossier avec ce nom existe déjà à cet emplacement' };
    }
    return { success: false, error: error.message };
  }
  
  return { success: true, folder: data };
}

// Get the next shooting number for auto-increment
export async function getNextShootingNumber(parentFolderId: string | null): Promise<number> {
  if (!parentFolderId) return 1;

  // Fast path: compute server-side (returns a single integer)
  const { data: rpcData, error: rpcError } = await supabase.rpc('get_next_shooting_number', {
    parent_folder: parentFolderId,
  });

  if (!rpcError && typeof rpcData === 'number') {
    return rpcData;
  }

  // Fallback: compute client-side (should be rarely used)
  if (rpcError) {
    console.warn('[rushesVirtualFolders] getNextShootingNumber rpc failed, falling back:', rpcError);
  }

  const { data, error } = await supabase
    .from('rushes_virtual_folders')
    .select('name')
    .eq('parent_folder_id', parentFolderId)
    .order('name', { ascending: true });

  if (error) {
    console.error('[rushesVirtualFolders] getNextShootingNumber fallback error:', error);
    return 1;
  }

  const folders = (data || []) as { name: string }[];
  let maxNumber = 0;
  for (const folder of folders) {
    const num = Number.parseInt(folder.name, 10);
    if (!Number.isNaN(num) && num > maxNumber) maxNumber = num;
  }
  return maxNumber + 1;
}


export async function createShootingFolder(
  name: string,
  parentFolderId: string | null = null
): Promise<{ success: boolean; folder?: VirtualFolder; error?: string }> {
  // Create the main shooting folder
  const result = await createVirtualFolder(name, parentFolderId);
  
  if (!result.success || !result.folder) {
    return result;
  }
  
  // Create subfolders inside the shooting folder (parallel for performance)
  const subfolders = ['Assets', 'Derush', 'Montage'];
  await Promise.all(
    subfolders.map(async (subfolder) => {
      const subResult = await createVirtualFolder(subfolder, result.folder.id);
      if (!subResult.success) {
        console.warn(`[createShootingFolder] Failed to create subfolder ${subfolder}:`, subResult.error);
      }
    })
  );
  
  return result;
}

export async function renameVirtualFolder(
  folderId: string,
  newName: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('rushes_virtual_folders')
    .update({ name: newName })
    .eq('id', folderId);
  
  if (error) {
    console.error('[rushesVirtualFolders] renameVirtualFolder error:', error);
    if (error.code === '23505') {
      return { success: false, error: 'Un dossier avec ce nom existe déjà à cet emplacement' };
    }
    return { success: false, error: error.message };
  }
  
  return { success: true };
}

export async function moveVirtualFolder(
  folderId: string,
  newParentFolderId: string | null
): Promise<{ success: boolean; error?: string }> {
  // Prevent moving a folder into itself or its descendants
  if (newParentFolderId) {
    const descendants = await getFolderDescendants(folderId);
    if (descendants.includes(newParentFolderId) || folderId === newParentFolderId) {
      return { success: false, error: 'Impossible de déplacer un dossier dans lui-même ou un sous-dossier' };
    }
  }
  
  const { error } = await supabase
    .from('rushes_virtual_folders')
    .update({ parent_folder_id: newParentFolderId })
    .eq('id', folderId);
  
  if (error) {
    console.error('[rushesVirtualFolders] moveVirtualFolder error:', error);
    if (error.code === '23505') {
      return { success: false, error: 'Un dossier avec ce nom existe déjà à cet emplacement' };
    }
    return { success: false, error: error.message };
  }
  
  return { success: true };
}

export async function deleteVirtualFolder(
  folderId: string
): Promise<{ success: boolean; error?: string }> {
  // CASCADE will handle deleting child folders and orphaning files
  const { error } = await supabase
    .from('rushes_virtual_folders')
    .delete()
    .eq('id', folderId);
  
  if (error) {
    console.error('[rushesVirtualFolders] deleteVirtualFolder error:', error);
    return { success: false, error: error.message };
  }
  
  return { success: true };
}

// Get all descendant folder IDs (for preventing circular moves)
async function getFolderDescendants(folderId: string): Promise<string[]> {
  const descendants: string[] = [];
  const queue = [folderId];
  
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const { data } = await supabase
      .from('rushes_virtual_folders')
      .select('id')
      .eq('parent_folder_id', currentId);
    
    if (data) {
      for (const folder of data) {
        descendants.push(folder.id);
        queue.push(folder.id);
      }
    }
  }
  
  return descendants;
}

// ============ FILE OPERATIONS ============

export async function getVirtualFiles(folderId: string | null = null): Promise<VirtualFile[]> {
  const query = supabase
    .from('rushes_virtual_files')
    .select('*')
    .order('display_name', { ascending: true });
  
  if (folderId === null) {
    query.is('folder_id', null);
  } else {
    query.eq('folder_id', folderId);
  }
  
  const { data, error } = await query;
  
  if (error) {
    console.error('[rushesVirtualFolders] getVirtualFiles error:', error);
    return [];
  }
  
  return data || [];
}

export async function getVirtualFileByS3Key(s3Key: string): Promise<VirtualFile | null> {
  const { data, error } = await supabase
    .from('rushes_virtual_files')
    .select('*')
    .eq('s3_key', s3Key)
    .maybeSingle();
  
  if (error) {
    console.error('[rushesVirtualFolders] getVirtualFileByS3Key error:', error);
    return null;
  }
  
  return data;
}

// Check if a file with the same display_name AND file_size already exists
// This helps detect duplicate files that were imported from different S3 paths
async function checkForDuplicateFile(
  displayName: string,
  fileSize: number | null
): Promise<VirtualFile | null> {
  // Only check if we have a valid file size (> 0)
  if (!fileSize || fileSize <= 0) {
    return null;
  }

  const { data, error } = await supabase
    .from('rushes_virtual_files')
    .select('*')
    .eq('display_name', displayName)
    .eq('file_size', fileSize)
    .limit(1)
    .maybeSingle();
  
  if (error) {
    console.error('[rushesVirtualFolders] checkForDuplicateFile error:', error);
    return null;
  }
  
  return data;
}

export async function createVirtualFile(
  s3Key: string,
  displayName: string,
  folderId: string | null = null,
  fileSize: number | null = null,
  lastModified: string | null = null
): Promise<{ success: boolean; file?: VirtualFile; error?: string }> {
  const { data: userData } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('rushes_virtual_files')
    .insert({
      s3_key: s3Key,
      display_name: displayName,
      folder_id: folderId,
      file_size: fileSize,
      last_modified: lastModified,
      created_by: userData?.user?.id || null,
    })
    .select()
    .single();
  
  if (error) {
    console.error('[rushesVirtualFolders] createVirtualFile error:', error);
    if (error.code === '23505') {
      return { success: false, error: 'Ce fichier est déjà enregistré' };
    }
    return { success: false, error: error.message };
  }
  
  return { success: true, file: data };
}

export async function renameVirtualFile(
  fileId: string,
  newDisplayName: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('rushes_virtual_files')
    .update({ display_name: newDisplayName })
    .eq('id', fileId);
  
  if (error) {
    console.error('[rushesVirtualFolders] renameVirtualFile error:', error);
    return { success: false, error: error.message };
  }
  
  return { success: true };
}

export async function moveVirtualFile(
  fileId: string,
  newFolderId: string | null
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('rushes_virtual_files')
    .update({ folder_id: newFolderId })
    .eq('id', fileId);
  
  if (error) {
    console.error('[rushesVirtualFolders] moveVirtualFile error:', error);
    return { success: false, error: error.message };
  }
  
  return { success: true };
}

export async function assignVirtualFile(
  fileId: string,
  customerId: string | null,
  shootingId: string | null
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('rushes_virtual_files')
    .update({ customer_id: customerId, shooting_id: shootingId })
    .eq('id', fileId);

  if (error) {
    console.error('[rushesVirtualFolders] assignVirtualFile error:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

export async function deleteVirtualFile(
  fileId: string
): Promise<{ success: boolean; error?: string }> {
  // 1. Fetch the s3_key before deleting the DB row
  const { data: fileData, error: fetchError } = await supabase
    .from('rushes_virtual_files')
    .select('s3_key')
    .eq('id', fileId)
    .maybeSingle();

  if (fetchError) {
    console.error('[rushesVirtualFolders] deleteVirtualFile fetch error:', fetchError);
    return { success: false, error: fetchError.message };
  }

  // 2. Delete from S3 if we have a key (best-effort, don't block DB deletion)
  if (fileData?.s3_key) {
    try {
      const { data: s3Result, error: s3Error } = await supabase.functions.invoke('s3-files', {
        body: { action: 'delete', key: fileData.s3_key }
      });
      if (s3Error || s3Result?.error) {
        console.warn('[rushesVirtualFolders] S3 delete failed (continuing with DB delete):', s3Error?.message || s3Result?.error);
      }
    } catch (err) {
      console.warn('[rushesVirtualFolders] S3 delete exception (continuing with DB delete):', err);
    }
  }

  // 3. Delete from DB
  const { error } = await supabase
    .from('rushes_virtual_files')
    .delete()
    .eq('id', fileId);
  
  if (error) {
    console.error('[rushesVirtualFolders] deleteVirtualFile error:', error);
    return { success: false, error: error.message };
  }
  
  return { success: true };
}

// ============ COMBINED OPERATIONS ============

// Get both folders and files for a given parent, merged into VirtualItems
export async function getVirtualItems(parentFolderId: string | null = null): Promise<VirtualItem[]> {
  const [folders, files] = await Promise.all([
    getVirtualFolders(parentFolderId),
    getVirtualFiles(parentFolderId),
  ]);
  
  const items: VirtualItem[] = [];
  
  // Add folders
  for (const folder of folders) {
    items.push({
      id: folder.id,
      name: folder.name,
      isFolder: true,
      size: 0,
      lastModified: folder.updated_at,
      folderId: folder.id,
      parentFolderId: folder.parent_folder_id,
      createdBy: folder.created_by,
    });
  }
  
  // Add files
  for (const file of files) {
    items.push({
      id: file.id,
      name: file.display_name,
      isFolder: false,
      size: file.file_size || 0,
      lastModified: file.last_modified || file.updated_at,
      s3Key: file.s3_key,
      parentFolderId: file.folder_id,
    });
  }
  
  // Sort: At root level (parentFolderId === null), files first then folders
  // Inside folders, folders first then files
  // Both sorted alphabetically (case-insensitive)
  const isRootLevel = parentFolderId === null;
  
  return items.sort((a, b) => {
    // Different sorting based on level
    if (isRootLevel) {
      // Root: files (untracked) first, then folders (clients)
      if (!a.isFolder && b.isFolder) return -1;
      if (a.isFolder && !b.isFolder) return 1;
    } else {
      // Inside folders: folders first, then files
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
    }
    // Alphabetical sort (case-insensitive)
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase(), 'fr', { numeric: true });
  });
}

// Build folder path (breadcrumb) from root to given folder
export async function getFolderPath(folderId: string | null): Promise<VirtualFolder[]> {
  if (!folderId) return [];
  
  const path: VirtualFolder[] = [];
  let currentId: string | null = folderId;
  
  while (currentId) {
    const folder = await getVirtualFolderById(currentId);
    if (!folder) break;
    path.unshift(folder);
    currentId = folder.parent_folder_id;
  }
  
  return path;
}

// Bulk move files to a folder
export async function bulkMoveFiles(
  fileIds: string[],
  destinationFolderId: string | null
): Promise<{ success: boolean; movedCount: number; errorCount: number }> {
  let movedCount = 0;
  let errorCount = 0;
  
  for (const fileId of fileIds) {
    const result = await moveVirtualFile(fileId, destinationFolderId);
    if (result.success) {
      movedCount++;
    } else {
      errorCount++;
    }
  }
  
  return { success: errorCount === 0, movedCount, errorCount };
}

// Bulk move folders
export async function bulkMoveFolders(
  folderIds: string[],
  destinationFolderId: string | null
): Promise<{ success: boolean; movedCount: number; errorCount: number }> {
  let movedCount = 0;
  let errorCount = 0;
  
  for (const folderId of folderIds) {
    const result = await moveVirtualFolder(folderId, destinationFolderId);
    if (result.success) {
      movedCount++;
    } else {
      errorCount++;
    }
  }
  
  return { success: errorCount === 0, movedCount, errorCount };
}

// ============ SHOOTING FOLDER RESOLUTION ============

type FolderBasic = { id: string; name: string };

/**
 * Find the client root folder by shortId (new format) or legacy name (fallback).
 */
function findClientRootFolder(
  rootFolders: FolderBasic[],
  clientShortId: string,
  user: { first_name?: string | null; last_name?: string | null } | null
): FolderBasic | null {
  // Match by embedded shortId (new format: Prenom_Nom_shortId)
  const byShortId = rootFolders.find(f => {
    const embedded = extractIdFromFolderName(f.name);
    return embedded && embedded.toLowerCase() === clientShortId;
  });
  if (byShortId) return byShortId;

  // Fallback: match by legacy name (case-insensitive)
  const firstName = (user?.first_name || '').trim();
  const lastName = (user?.last_name || '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  if (!fullName) return null;

  return rootFolders.find(f => f.name.toLowerCase() === fullName.toLowerCase()) || null;
}

/**
 * Find the shooting folder by shortId (new format) or legacy numeric index (fallback).
 */
async function findShootingFolderByShortIdOrLegacy(
  subfolders: FolderBasic[],
  shootingShortId: string,
  userId: string,
  shootingId: string
): Promise<FolderBasic | null> {
  // Match by embedded shortId (new format: Tournage_YYYYMMDD_shortId)
  const byShortId = subfolders.find(f => {
    const embedded = extractIdFromFolderName(f.name);
    return embedded && embedded.toLowerCase() === shootingShortId;
  });
  if (byShortId) return byShortId;

  // Fallback: match by old numeric format (1, 2, 3...)
  const { data: shootings } = await supabase
    .from('shootings')
    .select('id, date')
    .eq('user_id', userId)
    .order('date', { ascending: true });

  if (!shootings) return null;

  const shootingIndex = shootings.findIndex(s => s.id === shootingId) + 1;
  if (shootingIndex <= 0) return null;

  return subfolders.find(f => f.name === String(shootingIndex)) || null;
}

/**
 * Given a real shooting UUID (from the `shootings` table), find the ID of the
 * `Assets` virtual subfolder inside the corresponding `Tournage_YYYYMMDD_shortId`
 * folder in the client's root folder tree.
 *
 * Returns null if the folder cannot be found (e.g. old shooting without virtual folder).
 */
export async function findShootingAssetsFolder(shootingId: string): Promise<string | null> {
  try {
    // 1. Load the shooting to get user_id and client info
    const { data: shooting, error: shootingError } = await supabase
      .from('shootings')
      .select('id, user_id, date, users!inner(first_name, last_name)')
      .eq('id', shootingId)
      .maybeSingle();

    if (shootingError || !shooting) {
      console.warn('[findShootingAssetsFolder] Shooting not found:', shootingId);
      return null;
    }

    // 2. Compute the short IDs
    const shootingShortId = shooting.id.replaceAll('-', '').slice(0, 8).toLowerCase();
    const clientShortId = shooting.user_id.replaceAll('-', '').slice(0, 8).toLowerCase();

    // 3. Find the client's root folder
    const { data: rootFolders, error: rootError } = await supabase
      .from('rushes_virtual_folders')
      .select('id, name')
      .is('parent_folder_id', null);

    if (rootError || !rootFolders) {
      console.warn('[findShootingAssetsFolder] Failed to load root folders:', rootError);
      return null;
    }

    const clientFolder = findClientRootFolder(rootFolders, clientShortId, shooting.users as any);
    if (!clientFolder) {
      console.warn('[findShootingAssetsFolder] No root folder found for client:', shooting.user_id);
      return null;
    }

    // 4. Find the Tournage folder inside the client root
    const { data: clientSubfolders, error: subError } = await supabase
      .from('rushes_virtual_folders')
      .select('id, name')
      .eq('parent_folder_id', clientFolder.id);

    if (subError || !clientSubfolders) {
      console.warn('[findShootingAssetsFolder] Failed to load client subfolders:', subError);
      return null;
    }

    const shootingFolder = await findShootingFolderByShortIdOrLegacy(
      clientSubfolders, shootingShortId, shooting.user_id, shootingId
    );
    if (!shootingFolder) {
      console.warn('[findShootingAssetsFolder] No Tournage folder found for shooting:', shootingId, '(shortId:', shootingShortId, ')');
      return null;
    }

    // 5. Find the Assets subfolder inside the shooting folder
    const { data: shootingSubfolders, error: assetsError } = await supabase
      .from('rushes_virtual_folders')
      .select('id, name')
      .eq('parent_folder_id', shootingFolder.id);

    if (assetsError || !shootingSubfolders) {
      console.warn('[findShootingAssetsFolder] Failed to load shooting subfolders:', assetsError);
      return null;
    }

    const assetsFolder = shootingSubfolders.find(f => f.name.toLowerCase() === 'assets');
    if (!assetsFolder) {
      console.warn('[findShootingAssetsFolder] No Assets subfolder found in shooting folder:', shootingFolder.id);
      return null;
    }

    return assetsFolder.id;
  } catch (error) {
    console.error('[findShootingAssetsFolder] Unexpected error:', error);
    return null;
  }
}

// ============ S3 IMPORT OPERATIONS ============

// S3 import functions (uses listS3Objects imported at top)

// Import all files from S3 into the virtual file system
// This scans S3 recursively and creates virtual file entries for files not yet registered
export async function importS3FilesToVirtualSystem(
  folderId: string | null = null,
  s3Prefix: string = ''
): Promise<{ success: boolean; imported: number; skipped: number; error?: string }> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user?.id) {
    return { success: false, imported: 0, skipped: 0, error: 'Non authentifié' };
  }

  let imported = 0;
  let skipped = 0;

  try {
    // List S3 objects at this prefix
    const result = await listS3Objects(s3Prefix);
    if (!result.success || !result.data) {
      return { success: false, imported: 0, skipped: 0, error: result.error || 'Erreur S3' };
    }

    for (const obj of result.data) {
      if (obj.isFolder) {
        // Skip folders - they're handled separately in the virtual system
        continue;
      }

      // Check if this file is already registered by s3_key
      const existing = await getVirtualFileByS3Key(obj.key);
      if (existing) {
        skipped++;
        continue;
      }

      // Check if a file with the same name AND size already exists (duplicate detection)
      // This prevents importing the same physical file that exists at different S3 paths
      const duplicateCheck = await checkForDuplicateFile(obj.name, obj.size);
      if (duplicateCheck) {
        console.log(`[importS3Files] Skipping duplicate: ${obj.key} (matches ${duplicateCheck.s3_key} by name+size)`);
        skipped++;
        continue;
      }

      // Create virtual file entry
      const createResult = await createVirtualFile(
        obj.key,
        obj.name,
        folderId,
        obj.size,
        obj.lastModified
      );

      if (createResult.success) {
        imported++;
      } else {
        console.warn(`[importS3Files] Failed to import ${obj.key}:`, createResult.error);
        skipped++;
      }
    }

    return { success: true, imported, skipped };
  } catch (error) {
    console.error('[importS3Files] Error:', error);
    return { success: false, imported, skipped, error: error instanceof Error ? error.message : 'Erreur inconnue' };
  }
}

// Helper type for import statistics
interface ImportStats {
  foldersCreated: number;
  filesImported: number;
}

// Helper: Process a single S3 folder and return its virtual folder ID
async function processS3Folder(
  folder: { key: string; name: string },
  parentFolderId: string | null,
  stats: ImportStats
): Promise<string | null> {
  const folderName = folder.name.replace(/\/$/, '');

  // Check if folder already exists
  const existingFolders = await getVirtualFolders(parentFolderId);
  const existing = existingFolders.find(f => f.name === folderName);

  if (existing) {
    return existing.id;
  }

  const createResult = await createVirtualFolder(folderName, parentFolderId);
  if (createResult.success && createResult.folder) {
    stats.foldersCreated++;
    return createResult.folder.id;
  }

  return null;
}

// Helper: Import a single S3 file into the virtual system
async function importS3File(
  file: { key: string; name: string; size: number; lastModified: string },
  parentFolderId: string | null,
  stats: ImportStats
): Promise<void> {
  const existing = await getVirtualFileByS3Key(file.key);
  if (existing) return;

  // Check for duplicates by name+size
  const duplicate = await checkForDuplicateFile(file.name, file.size);
  if (duplicate) {
    console.log(`[importS3WithStructure] Skipping duplicate: ${file.key} (matches ${duplicate.s3_key} by name+size)`);
    return;
  }

  const createResult = await createVirtualFile(
    file.key,
    file.name,
    parentFolderId,
    file.size,
    file.lastModified
  );

  if (createResult.success) {
    stats.filesImported++;
  }
}

// Import S3 files recursively, creating virtual folders to match S3 structure
export async function importS3WithStructure(
  s3Prefix: string = '',
  parentFolderId: string | null = null
): Promise<{ success: boolean; foldersCreated: number; filesImported: number; error?: string }> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user?.id) {
    return { success: false, foldersCreated: 0, filesImported: 0, error: 'Non authentifié' };
  }

  const stats: ImportStats = { foldersCreated: 0, filesImported: 0 };

  try {
    // List S3 objects at this prefix
    const result = await listS3Objects(s3Prefix);
    if (!result.success || !result.data) {
      return { success: false, foldersCreated: 0, filesImported: 0, error: result.error || 'Erreur S3' };
    }

    // Process folders first
    const folders = result.data.filter(obj => obj.isFolder);
    const files = result.data.filter(obj => !obj.isFolder);

    // Create virtual folders for each S3 folder and recursively import
    for (const folder of folders) {
      const subFolderId = await processS3Folder(folder, parentFolderId, stats);

      if (subFolderId) {
        const subResult = await importS3WithStructure(folder.key, subFolderId);
        stats.foldersCreated += subResult.foldersCreated;
        stats.filesImported += subResult.filesImported;
      }
    }

    // Import files into current folder
    for (const file of files) {
      await importS3File(file, parentFolderId, stats);
    }

    return { success: true, ...stats };
  } catch (error) {
    console.error('[importS3WithStructure] Error:', error);
    return { success: false, ...stats, error: error instanceof Error ? error.message : 'Erreur inconnue' };
  }
}

// ============ SHOOTING FOLDER AUTOMATION ============

/**
 * Find the Tournage_YYYYMMDD_shortId folder for a given shooting ID.
 * Uses the shortId embedded in the folder name (new format).
 */
export async function findShootingFolderNew(shootingId: string): Promise<{ id: string; name: string } | null> {
  try {
    const { data: shooting, error } = await supabase
      .from('shootings')
      .select('id, user_id, date')
      .eq('id', shootingId)
      .maybeSingle();

    if (error || !shooting) {
      console.warn('[findShootingFolderNew] Shooting not found:', shootingId);
      return null;
    }

    const shootingShortId = shooting.id.replaceAll('-', '').slice(0, 8).toLowerCase();
    const clientShortId = shooting.user_id.replaceAll('-', '').slice(0, 8).toLowerCase();

    // Find client root folder
    const { data: rootFolders } = await supabase
      .from('rushes_virtual_folders')
      .select('id, name')
      .is('parent_folder_id', null);

    const clientFolder = (rootFolders || []).find(f => {
      const embedded = extractIdFromFolderName(f.name);
      return embedded && embedded.toLowerCase() === clientShortId;
    });

    if (!clientFolder) {
      console.warn('[findShootingFolderNew] No root folder for client:', shooting.user_id);
      return null;
    }

    // Find Tournage_YYYYMMDD_shortId inside client folder
    const { data: subfolders } = await supabase
      .from('rushes_virtual_folders')
      .select('id, name')
      .eq('parent_folder_id', clientFolder.id);

    const tournageFolder = (subfolders || []).find(f => {
      const embedded = extractIdFromFolderName(f.name);
      return embedded && embedded.toLowerCase() === shootingShortId;
    });

    return tournageFolder || null;
  } catch (error) {
    console.error('[findShootingFolderNew] Error:', error);
    return null;
  }
}

/**
 * Ensure the Tournage_YYYYMMDD_shortId folder exists for a shooting.
 * Idempotent: creates it (with Assets/Derush/Montage) only if missing.
 * Returns the shooting folder ID, or null on failure.
 */
export async function ensureShootingFolder(
  shootingId: string,
  userId: string,
  date: string | null
): Promise<string | null> {
  try {
    // Check if already exists
    const existing = await findShootingFolderNew(shootingId);
    if (existing) return existing.id;

    // Find the client root folder
    const clientShortId = userId.replaceAll('-', '').slice(0, 8).toLowerCase();
    const { data: rootFolders } = await supabase
      .from('rushes_virtual_folders')
      .select('id, name')
      .is('parent_folder_id', null);

    const clientFolder = (rootFolders || []).find(f => {
      const embedded = extractIdFromFolderName(f.name);
      return embedded && embedded.toLowerCase() === clientShortId;
    });

    if (!clientFolder) {
      console.warn('[ensureShootingFolder] No root folder for client:', userId);
      return null;
    }

    // Build folder name: Tournage_YYYYMMDD_shortId
    const shootingShortId = shootingId.replaceAll('-', '').slice(0, 8);
    const dateStr = (date ?? new Date().toISOString()).slice(0, 10).replaceAll('-', '');
    const tournageFolderName = `Tournage_${dateStr}_${shootingShortId}`;

    // Create the main shooting folder
    const { data: newFolder, error: insertError } = await supabase
      .from('rushes_virtual_folders')
      .insert({ name: tournageFolderName, parent_folder_id: clientFolder.id, created_by: null })
      .select()
      .single();

    if (insertError) {
      if (insertError.code === '23505') {
        // Already exists (race condition) — re-fetch
        const retry = await findShootingFolderNew(shootingId);
        return retry?.id || null;
      }
      console.warn('[ensureShootingFolder] Failed to create folder:', insertError);
      return null;
    }

    // Create Assets / Derush / Montage subfolders in parallel
    await Promise.all(['Assets', 'Derush', 'Montage'].map(name =>
      supabase.from('rushes_virtual_folders').insert({
        name,
        parent_folder_id: newFolder.id,
        created_by: null,
      })
    ));

    console.log('[ensureShootingFolder] Created:', tournageFolderName);
    return newFolder.id;
  } catch (error) {
    console.error('[ensureShootingFolder] Error:', error);
    return null;
  }
}

/**
 * Ensure the topic folder exists for a video_content inside its shooting folder.
 * Idempotent: uses the shortId embedded in the folder name.
 * Returns { topicFolderId, rushesFolderId } or null on failure.
 */
export async function ensureTopicFolder(
  shootingId: string,
  topicTitle: string,
  videoContentId: string
): Promise<{ topicFolderId: string; rushesFolderId: string } | null> {
  try {
    const contentShortId = videoContentId.replaceAll('-', '').slice(0, 8).toLowerCase();

    // Check if topic folder already exists (search across all folders by shortId)
    const { data: existingFolders } = await supabase
      .from('rushes_virtual_folders')
      .select('id, name, parent_folder_id')
      .like('name', `%_${contentShortId}`);

    if (existingFolders && existingFolders.length > 0) {
      const topicFolder = existingFolders[0];
      // Find Rushes subfolder
      const { data: subfolders } = await supabase
        .from('rushes_virtual_folders')
        .select('id, name')
        .eq('parent_folder_id', topicFolder.id)
        .eq('name', 'Rushes')
        .maybeSingle();
      if (subfolders) {
        return { topicFolderId: topicFolder.id, rushesFolderId: subfolders.id };
      }
    }

    // Find or create shooting folder
    const shootingFolder = await findShootingFolderNew(shootingId);
    if (!shootingFolder) {
      console.warn('[ensureTopicFolder] Shooting folder not found for:', shootingId);
      return null;
    }

    // Create topic folder
    const folderName = buildTopicFolderNameWithId(topicTitle || 'Sans titre', videoContentId);
    const { data: topicFolder, error: topicError } = await supabase
      .from('rushes_virtual_folders')
      .insert({ name: folderName, parent_folder_id: shootingFolder.id, created_by: null })
      .select()
      .single();

    if (topicError) {
      if (topicError.code === '23505') {
        // Already exists — retry fetch
        const retry = await ensureTopicFolder(shootingId, topicTitle, videoContentId);
        return retry;
      }
      console.warn('[ensureTopicFolder] Failed to create topic folder:', topicError);
      return null;
    }

    // Create Rushes / Derush / Montage in parallel
    const [rushesResult] = await Promise.all([
      supabase.from('rushes_virtual_folders').insert({ name: 'Rushes', parent_folder_id: topicFolder.id, created_by: null }).select().single(),
      supabase.from('rushes_virtual_folders').insert({ name: 'Derush', parent_folder_id: topicFolder.id, created_by: null }),
      supabase.from('rushes_virtual_folders').insert({ name: 'Montage', parent_folder_id: topicFolder.id, created_by: null }),
    ]);

    if (rushesResult.error || !rushesResult.data) {
      console.warn('[ensureTopicFolder] Failed to create Rushes subfolder:', rushesResult.error);
      return null;
    }

    console.log('[ensureTopicFolder] Created:', folderName);
    return { topicFolderId: topicFolder.id, rushesFolderId: rushesResult.data.id };
  } catch (error) {
    console.error('[ensureTopicFolder] Error:', error);
    return null;
  }
}

/**
 * Rename the topic virtual folder when the video_content title changes.
 * Non-blocking: logs errors but does not throw.
 */
export async function renameTopicFolder(videoContentId: string, newTitle: string): Promise<void> {
  try {
    const contentShortId = videoContentId.replaceAll('-', '').slice(0, 8).toLowerCase();

    const { data: folders } = await supabase
      .from('rushes_virtual_folders')
      .select('id, name')
      .like('name', `%_${contentShortId}`);

    if (!folders || folders.length === 0) return;

    const newName = buildTopicFolderNameWithId(newTitle || 'Sans titre', videoContentId);
    await supabase
      .from('rushes_virtual_folders')
      .update({ name: newName })
      .eq('id', folders[0].id);

    console.log('[renameTopicFolder] Renamed to:', newName);
  } catch (error) {
    console.warn('[renameTopicFolder] Error (non-blocking):', error);
  }
}

// ============ TOPIC/VIDEO FOLDER CREATION ============

/**
 * Sanitize a folder name for S3 compatibility
 * Removes or replaces special characters that could cause issues
 */
export function sanitizeFolderName(name: string): string {
  return name
    // Replace accented characters with their base form
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    // Replace common problematic characters
    .replaceAll(/[<>:"/\\|?*]/g, '-')
    // Replace multiple spaces/dashes with single dash
    .replaceAll(/[\s-]+/g, '-')
    // Remove leading/trailing dashes and spaces
    .replaceAll(/(^[-\s]+)|([-\s]+$)/g, '')
    // Limit length to 100 characters
    .slice(0, 100)
    // Fallback if empty
    || 'sans-titre';
}

/**
 * Find the shooting folder for a given shooting ID
 * Shooting folders are numbered (1, 2, 3...) inside the customer folder
 */
export async function findShootingFolder(
  shootingId: string
): Promise<VirtualFolder | null> {
  // First get the shooting details to find the customer
  const { data: shooting, error: shootingError } = await supabase
    .from('shootings')
    .select('user_id, users!inner(first_name, last_name, email)')
    .eq('id', shootingId)
    .single();

  if (shootingError || !shooting) {
    console.error('[findShootingFolder] Error fetching shooting:', shootingError);
    return null;
  }

  const userId = shooting.user_id;

  // Strategy 1: Find customer folder by embedded client ID (new format)
  const { data: allRootFolders } = await supabase
    .from('rushes_virtual_folders')
    .select('id, name')
    .is('parent_folder_id', null);

  let customerFolder: { id: string; name: string } | null = null;

  if (allRootFolders) {
    // Try matching by embedded short ID first
    const shortClientId = userId.replaceAll('-', '').slice(0, 8).toLowerCase();
    customerFolder = allRootFolders.find(f => {
      const embeddedId = extractIdFromFolderName(f.name);
      return embeddedId && embeddedId.toLowerCase() === shortClientId;
    }) || null;

    // Fallback: match by legacy name (case-insensitive)
    if (!customerFolder) {
      const user = shooting.users as any;
      const firstName = user.first_name?.trim() || '';
      const lastName = user.last_name?.trim() || '';
      let customerFolderName = '';
      if (firstName && lastName) {
        customerFolderName = `${firstName} ${lastName}`;
      } else if (firstName) {
        customerFolderName = firstName;
      } else if (lastName) {
        customerFolderName = lastName;
      } else {
        customerFolderName = user.email.split('@')[0];
      }
      customerFolder = allRootFolders.find(f => 
        f.name.toLowerCase() === customerFolderName.toLowerCase()
      ) || null;
    }
  }

  if (!customerFolder) {
    console.error('[findShootingFolder] Customer folder not found for user:', userId);
    return null;
  }

  // Get all shootings for this customer ordered by date to find the shooting number
  const { data: shootings, error: shootingsError } = await supabase
    .from('shootings')
    .select('id, date')
    .eq('user_id', userId)
    .order('date', { ascending: true });

  if (shootingsError || !shootings) {
    console.error('[findShootingFolder] Error fetching shootings:', shootingsError);
    return null;
  }

  // Find the index of this shooting (1-based)
  const shootingIndex = shootings.findIndex(s => s.id === shootingId) + 1;
  if (shootingIndex === 0) {
    console.error('[findShootingFolder] Shooting not found in list');
    return null;
  }

  // Find the folder with this number
  const { data: shootingFolder } = await supabase
    .from('rushes_virtual_folders')
    .select('*')
    .eq('parent_folder_id', customerFolder.id)
    .eq('name', String(shootingIndex))
    .maybeSingle();

  return shootingFolder;
}

/**
 * Create a topic folder with Rushes/Derush/Montage subfolders inside the shooting folder.
 * Uses the new Tournage_YYYYMMDD_shortId format (findShootingFolderNew).
 * Returns the Rushes folder ID for automatic file routing.
 */
export async function createTopicFolders(
  shootingId: string,
  topicTitle: string,
  videoContentId?: string
): Promise<{ success: boolean; topicFolderId?: string; rushesFolderId?: string; error?: string }> {
  try {
    if (videoContentId) {
      // Use the idempotent ensureTopicFolder path
      const result = await ensureTopicFolder(shootingId, topicTitle, videoContentId);
      if (!result) {
        return { success: false, error: 'Dossier du tournage introuvable ou erreur de création' };
      }
      return { success: true, topicFolderId: result.topicFolderId, rushesFolderId: result.rushesFolderId };
    }

    // Legacy path without videoContentId
    const shootingFolder = await findShootingFolderNew(shootingId);
    if (!shootingFolder) {
      return { success: false, error: 'Dossier du tournage introuvable' };
    }

    const folderName = sanitizeFolderName(topicTitle);
    const topicResult = await createVirtualFolder(folderName, shootingFolder.id);
    if (!topicResult.success || !topicResult.folder) {
      return { success: false, error: topicResult.error || 'Erreur création dossier sujet' };
    }

    // Create Rushes / Derush / Montage
    const [rushesResult] = await Promise.all([
      createVirtualFolder('Rushes', topicResult.folder.id),
      createVirtualFolder('Derush', topicResult.folder.id),
      createVirtualFolder('Montage', topicResult.folder.id),
    ]);

    return {
      success: true,
      topicFolderId: topicResult.folder.id,
      rushesFolderId: rushesResult.folder?.id,
    };
  } catch (error) {
    console.error('[createTopicFolders] Error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Erreur inconnue' };
  }
}
